import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { AnalystLevels } from '@/components/admin/protocoles/reading/analyst-levels';
import { AnalystPreviousReading } from '@/components/admin/protocoles/reading/analyst-previous-reading';
import { AnalystPassPanel } from '@/components/admin/protocoles/reading/analyst-reading-panel';
import { AnalystReadings } from '@/components/admin/protocoles/reading/analyst-readings';
import { AnalystSettings } from '@/components/admin/protocoles/reading/analyst-settings';
import { useMethodSource } from '@/components/admin/protocoles/reading/use-method';
import { analystEncartSentences, withAnalystLevel } from '@/lib/analyst-reading';
import type { AnalystPass, ProtocolAnalyst, ProtocolStepDraft } from '@matter_extractor/shared';
import { analystSectionComplete, analystSectionGaps } from '@matter_extractor/shared';
import { TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';

/**
 * La section Analyste de l'éditeur de protocole (analyste.md §3, §3.5 ; issue #196 ;
 * planches `AnalysteA` et `AnalysteAEtats`) : ce que l'analyste du noyau lit du
 * protocole, versionné et figé avec lui - les niveaux des objectifs, les deux
 * seuils et le poids du style, une lecture par passe, et les deux aperçus. Elle
 * remplace la vue « Lecture pour l'analyste » de #154 et #158, dont elle garde la
 * position dans l'adresse (`?vue=lecture`, N47).
 *
 * ## Two columns, like the view Rangement
 *
 * On the LEFT, from top to bottom: the yellow encart of an incomplete section, the
 * grey block of the reading of before #196 when there is one, « Niveaux des
 * objectifs », « Certitude et poids », then the two tabs and the reading of the one
 * chosen. On the RIGHT, « Ce que la passe N recevra » for the tab chosen, `sticky`
 * beside a left column that is long. Below `lg` they stack, each at its natural
 * height - `admin-responsive.spec.ts` holds every admin screen at zero lateral
 * overflow, and two columns of 180 px would hold nothing.
 *
 * ## The draft is written through three doors, one write each
 *
 * The levels go through `withAnalystLevel` into the draft's STEPS (the level is a
 * field of the objective, K1), the numbers and the readings into the draft's
 * `analyst`, and « Reprendre dans la passe 1 » into the `analyst` as well: the section
 * hands its parent a new `analyst` or a new list of steps and the parent writes
 * `onDraft` once. Nothing here reads a query but Méthode, which is only the panel's.
 *
 * ## The encart, and the mark on the segment
 *
 * Incomplete - a level no objective carries, or a reading that is empty (§3.6) - the
 * section says so in a yellow encart that names what is missing, and the segment of
 * the switch carries the yellow mark (`protocol-view-switch.tsx`), both read off
 * `analystSectionGaps` of `shared`, the same walk `check_protocol` and the boxes of
 * the experience read. A protocol of before #196 opens that way: every objective at
 * « Aucun », both readings empty.
 *
 * ## The tab is the section's, and Méthode is read at its mount
 *
 * The tab chosen (the pass) is the one fact the left column and the right share, so
 * it lives here, and « Reprendre dans la passe 1 » brings the first tab forward.
 * Méthode - the part of each panel its own version writes - is read when the section
 * MOUNTS: the editor keeps its views mounted, so the request leaves even while the
 * section is hidden, and on a protocol that does not exist yet.
 */

export interface AnalystSectionProps {
  /** The section as typed - the draft's, or the version « Voir » is showing. */
  readonly analyst: ProtocolAnalyst;
  /** The reading of before #196, kept in the content (K14): the grey block, when it is not empty. */
  readonly previousReading: string;
  /** The steps of that same content: the objectives set, cited and resolved on. */
  readonly steps: readonly ProtocolStepDraft[];
  /** « Voir », or a retired protocol: nothing is settable or typable. */
  readonly readOnly: boolean;
  readonly onAnalyst: (next: ProtocolAnalyst) => void;
  readonly onSteps: (next: readonly ProtocolStepDraft[]) => void;
}

export function AnalystSection({
  analyst,
  previousReading,
  steps,
  readOnly,
  onAnalyst,
  onSteps,
}: AnalystSectionProps) {
  const encartId = useId();
  const [pass, setPass] = useState<AnalystPass>(1);
  const method = useMethodSource();
  const gaps = analystSectionGaps(steps, analyst);
  const incomplete = !analystSectionComplete(gaps);
  const encart = analystEncartSentences(gaps);

  return (
    <div data-testid={PROTOCOL_TEST_IDS.analyst} className="grid items-start gap-6 lg:grid-cols-2">
      <div className="flex min-w-0 flex-col gap-6">
        {incomplete ? (
          <div
            data-testid={PROTOCOL_TEST_IDS.analystEncart}
            className="flex items-start gap-2.5 rounded-md border border-gray-200 border-l-4 border-l-jaune-deep bg-jaune-tint px-3.5 py-2.5"
          >
            <TriangleAlert aria-hidden className="mt-0.5 size-4.5 shrink-0 text-jaune-deep" />
            <p id={encartId} className="font-semibold text-gray-900 text-sm">
              {encart.lead}
              {encart.rest === '' ? null : <span className="font-normal"> {encart.rest}</span>}
            </p>
          </div>
        ) : null}
        <AnalystPreviousReading
          reading={previousReading}
          steps={steps}
          readOnly={readOnly}
          onTake={() => {
            // One write, in the draft's `analyst`: the reading of before REPLACES the first.
            onAnalyst({ ...analyst, functionsReading: previousReading });
            setPass(1);
          }}
        />
        <AnalystLevels
          steps={steps}
          readOnly={readOnly}
          encartId={incomplete ? encartId : null}
          onLevel={(objectiveId, level) => onSteps(withAnalystLevel(steps, objectiveId, level))}
        />
        <AnalystSettings analyst={analyst} readOnly={readOnly} onChange={onAnalyst} />
        <AnalystReadings
          analyst={analyst}
          steps={steps}
          readOnly={readOnly}
          pass={pass}
          onPass={setPass}
          encartId={incomplete ? encartId : null}
          onChange={onAnalyst}
        />
      </div>

      <AnalystPassPanel
        pass={pass}
        reading={pass === 1 ? analyst.functionsReading : analyst.styleReading}
        steps={steps}
        method={method}
      />
    </div>
  );
}
